import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authApi, { TutorialResponse, VideoResponse } from '../../services/authApi';
import { useBadge } from '../../context/BadgeContext';
import './tool-page.css';
import './CoursesPage.css';

const DUMMY_TUTORIALS: TutorialResponse[] = [
  { id: 1, title: 'SwingTrading Foundations',    description: 'Learn the core principles of swing trading — structure, trend, and timing.',               badge: '📊', tags: ['Foundations', 'Beginner'],    orderIndex: 1, isFree: true,  isActive: true, videoCount: 2 },
  { id: 2, title: 'Trade Journal Mastery',        description: 'Build a journaling habit that improves your edge over time.',                              badge: '📓', tags: ['Journal', 'Mindset'],         orderIndex: 2, isFree: true,  isActive: true, videoCount: 2 },
  { id: 3, title: 'Bull Flag Pattern Deep Dive',  description: 'Identify, plan, and execute bull flag entries with precision.',                            badge: '🚀', tags: ['Pattern', 'Intermediate'],   orderIndex: 3, isFree: false, isActive: true, videoCount: 2 },
  { id: 4, title: 'Volume Analysis',              description: 'Use volume to confirm breakouts and avoid fakeouts.',                                      badge: '📈', tags: ['Volume', 'Advanced'],        orderIndex: 4, isFree: false, isActive: true, videoCount: 2 },
  { id: 5, title: 'RSI & Momentum Strategies',    description: 'Apply RSI in trending markets for high-probability pullback entries.',                    badge: '⚡', tags: ['RSI', 'Momentum'],           orderIndex: 5, isFree: false, isActive: true, videoCount: 1 },
  { id: 6, title: 'Breakout Trading Playbook',    description: 'A full system for trading range breakouts on daily and weekly charts.',                    badge: '🔓', tags: ['Breakout', 'System'],        orderIndex: 6, isFree: false, isActive: true, videoCount: 2 },
];

const DUMMY_VIDEOS: Record<number, VideoResponse[]> = {
  1: [
    { id: 1, tutorialId: 1, title: 'What is Swing Trading?',   description: 'Definition, timeframe, and how it differs from intraday.', videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', durationMin: 12, orderIndex: 1, locked: false },
    { id: 2, tutorialId: 1, title: 'Finding the Trend',        description: 'How to identify primary trend direction using higher timeframes.', videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', durationMin: 18, orderIndex: 2, locked: false },
  ],
  2: [
    { id: 3, tutorialId: 2, title: 'Why Most Traders Skip Journaling', description: 'The compounding edge of consistent review — with examples.', videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', durationMin: 10, orderIndex: 1, locked: false },
    { id: 4, tutorialId: 2, title: 'Trade Journal Template Walkthrough', description: 'Our exact spreadsheet template explained field by field.', videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ', durationMin: 22, orderIndex: 2, locked: false },
  ],
  3: [
    { id: 5, tutorialId: 3, title: 'Anatomy of a Bull Flag',          description: 'What makes a valid flag vs noise — criteria explained.', videoUrl: null, durationMin: 20, orderIndex: 1, locked: true },
    { id: 6, tutorialId: 3, title: 'Entry, Stop & Target Planning',   description: 'How to size, place your stop, and set realistic targets.', videoUrl: null, durationMin: 25, orderIndex: 2, locked: true },
  ],
  4: [
    { id: 7, tutorialId: 4, title: 'Volume Basics for Swing Traders', description: 'Accumulation vs distribution — reading volume the right way.', videoUrl: null, durationMin: 15, orderIndex: 1, locked: true },
    { id: 8, tutorialId: 4, title: 'Volume + Price Confluence',       description: 'Combining price action with volume for cleaner entries.', videoUrl: null, durationMin: 19, orderIndex: 2, locked: true },
  ],
  5: [
    { id: 9, tutorialId: 5, title: 'RSI in a Trending Market',        description: 'Using RSI 40-50 zone as a re-entry in uptrends.', videoUrl: null, durationMin: 17, orderIndex: 1, locked: true },
  ],
  6: [
    { id: 10, tutorialId: 6, title: 'Identifying a Valid Range',      description: 'Not every sideways move is a base — here is what to look for.', videoUrl: null, durationMin: 14, orderIndex: 1, locked: true },
    { id: 11, tutorialId: 6, title: 'Breakout Day Execution',         description: 'Pre-market prep, trigger candle, position sizing on breakout day.', videoUrl: null, durationMin: 23, orderIndex: 2, locked: true },
  ],
};

const CoursesPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [tutorials, setTutorials] = useState<TutorialResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState('All');
  const [search, setSearch] = useState('');
  const { markSeen } = useBadge();

  useEffect(() => { markSeen('LEARNING'); }, [markSeen]);

  // Drawer state — only used for locked (preview) courses
  const [selectedTutorial, setSelectedTutorial] = useState<TutorialResponse | null>(null);
  const [videos, setVideos] = useState<VideoResponse[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState<VideoResponse | null>(null);

  useEffect(() => {
    authApi.getTutorials()
      .then((res) => {
        setTutorials(res.data || []);
      })
      .catch(() => setTutorials([]))
      .finally(() => setLoading(false));
  }, []);

  const allTags = ['All', ...Array.from(new Set(tutorials.flatMap((t) => t.tags.map((tg) => tg.trim()))))];

  const visible = tutorials.filter((t) => {
    if (activeTag !== 'All' && !t.tags.map((tg) => tg.trim()).includes(activeTag)) return false;
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleOpen = async (tutorial: TutorialResponse) => {
    const hasAccess = tutorial.isFree || !!user?.isPremium;
    if (hasAccess) {
      // Navigate to full detail page
      navigate(`/courses/${tutorial.id}`, { state: { tutorial } });
      return;
    }
    // Locked — open preview drawer
    setSelectedTutorial(tutorial);
    setPlayingVideo(null);
    setVideosLoading(true);
    try {
      const res = await authApi.getTutorialVideos(tutorial.id);
      const data = res.data;
      // Backend returns videoUrl: null and locked: true for non-premium — trust it
      setVideos(data && data.length > 0 ? data : getDummyVideos(tutorial));
    } catch {
      setVideos(getDummyVideos(tutorial));
    } finally {
      setVideosLoading(false);
    }
  };

  const getDummyVideos = (tutorial: TutorialResponse): VideoResponse[] => {
    const isPremium = !!user?.isPremium;
    const base = DUMMY_VIDEOS[tutorial.id] || [];
    if (tutorial.isFree || isPremium) return base;
    return base.map((v) => ({ ...v, videoUrl: null, locked: true }));
  };

  const closeDrawer = () => {
    setSelectedTutorial(null);
    setVideos([]);
    setPlayingVideo(null);
  };

  const openLoginModal = () => window.dispatchEvent(new CustomEvent('open-login-modal'));

  const totalVideos = tutorials.reduce((s, t) => s + t.videoCount, 0);
  const freeCount = tutorials.filter((t) => t.isFree).length;

  return (
    <div className="tool-page">
      <div className="courses-hero">
        <p className="courses-eyebrow">Learning Library</p>
        <h1 className="courses-title">Video Tutorials</h1>
        <p className="courses-subtitle">Topic-based video tutorials — from foundations to advanced setups.</p>
        <div className="courses-stats">
          <div className="cstat-card">
            <span className="cstat-num">{tutorials.length}</span>
            <span className="cstat-label">Topics</span>
          </div>
          <div className="cstat-card">
            <span className="cstat-num">{totalVideos}</span>
            <span className="cstat-label">Videos</span>
          </div>
          <div className="cstat-card cstat-card--free">
            <span className="cstat-num">{freeCount}</span>
            <span className="cstat-label">Free</span>
          </div>
        </div>
      </div>

      <div className="courses-toolbar">
        <div className="courses-search-wrap">
          <SearchIcon />
          <input
            className="courses-search"
            placeholder="Search topics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {allTags.map((tag) => (
          <button
            key={tag}
            className={`courses-filter-btn ${activeTag === tag ? 'active' : ''}`}
            onClick={() => setActiveTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="courses-loading">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="course-skeleton" />)}
        </div>
      ) : (
        <div className="courses-grid">
          {visible.map((t) => (
            <TutorialCard
              key={t.id}
              tutorial={t}
              isPremium={!!user?.isPremium}
              isAuthenticated={isAuthenticated}
              onOpen={() => handleOpen(t)}
              onLoginPrompt={openLoginModal}
            />
          ))}
          {visible.length === 0 && (
            <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
              No tutorials match your filter.
            </div>
          )}
        </div>
      )}

      {/* Video drawer */}
      {selectedTutorial && (
        <div className="vd-overlay" onClick={closeDrawer}>
          <div className="vd-panel" onClick={(e) => e.stopPropagation()}>
            <div className="vd-header">
              <div className="vd-header-left">
                <span className="vd-badge">{selectedTutorial.badge}</span>
                <div>
                  <div className="vd-title">{selectedTutorial.title}</div>
                  <div className="vd-meta">
                    {selectedTutorial.videoCount} video{selectedTutorial.videoCount !== 1 ? 's' : ''}
                    {' · '}
                    <span className={selectedTutorial.isFree ? 'vd-tag-free' : 'vd-tag-pro'}>
                      {selectedTutorial.isFree ? 'Free' : 'Pro'}
                    </span>
                  </div>
                </div>
              </div>
              <button className="vd-close" onClick={closeDrawer} aria-label="Close">✕</button>
            </div>

            {playingVideo && (
              <div className="vd-player">
                <iframe
                  src={playingVideo.videoUrl + '?autoplay=1'}
                  title={playingVideo.title}
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                />
              </div>
            )}

            <div className="vd-list">
              {videosLoading ? (
                <div className="vd-loading">Loading videos...</div>
              ) : (
                videos.map((v, idx) => (
                  <div
                    key={v.id}
                    className={`vd-item ${v.locked ? 'vd-item--locked' : ''} ${playingVideo?.id === v.id ? 'vd-item--active' : ''}`}
                    onClick={() => {
                      if (v.locked) {
                        if (!isAuthenticated) openLoginModal();
                        else window.dispatchEvent(new CustomEvent('open-pricing'));
                      } else {
                        setPlayingVideo(v);
                      }
                    }}
                  >
                    <div className="vd-item-num">{idx + 1}</div>
                    <div className="vd-item-body">
                      <div className="vd-item-title">{v.title}</div>
                      {v.description && <div className="vd-item-desc">{v.description}</div>}
                    </div>
                    <div className="vd-item-right">
                      {v.locked
                        ? <LockIcon />
                        : <span className="vd-dur">{v.durationMin}m</span>
                      }
                    </div>
                  </div>
                ))
              )}
            </div>

            {!selectedTutorial.isFree && !user?.isPremium && (
              <div className="vd-upgrade">
                <span>🔒 Pro videos are locked.</span>
                <a href="/pricing" className="vd-upgrade-btn">Upgrade to Pro</a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface TutorialCardProps {
  tutorial: TutorialResponse;
  isPremium: boolean;
  isAuthenticated: boolean;
  onOpen: () => void;
  onLoginPrompt: () => void;
}

const TutorialCard: React.FC<TutorialCardProps> = ({ tutorial, isPremium, onOpen }) => {
  const locked = !tutorial.isFree && !isPremium;

  return (
    <div className={`t-card course-card ${locked ? 'course-card--locked' : ''}`}>
      <div className="course-card-top">
        <span className="course-icon">{tutorial.badge}</span>
        <span className={`pill ${tutorial.isFree ? 'pill-free' : 'pill-pro'}`}>
          {tutorial.isFree ? 'Free' : 'Pro'}
        </span>
      </div>

      <div className="course-title">{tutorial.title}</div>
      <p className="course-desc">{tutorial.description}</p>

      <div className="course-tags">
        {tutorial.tags.map((tag) => (
          <span key={tag} className="pill pill-tag">{tag.trim()}</span>
        ))}
      </div>

      <div className="course-meta">
        <span><VideoIcon />{tutorial.videoCount} video{tutorial.videoCount !== 1 ? 's' : ''}</span>
      </div>

      <button className="course-btn" onClick={onOpen}>
        {locked ? '🔒 Preview' : '▶ Watch'}
      </button>
    </div>
  );
};

export default CoursesPage;

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}
function VideoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
      <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  );
}
function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}
